import { Trans, useLingui } from "@lingui/react/macro";
import {
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Plus,
  Trash2,
} from "lucide-react";
import { Observer } from "mobx-react-lite";
import React, { useId, useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/utils/shadcn-ui";

import { snapshot } from "../../../utils/utils";
import { Tooltip } from "../base/tooltip";
import { Button } from "../button";

type WithRowId = { __rowId: string };

/**
 * Add/remove/expand state for a RowAccordion, including the rule that removing
 * the expanded row expands what took its place (or the row above, if it was the
 * last). `EventsDraft` (src/features/patient/events/events-draft.ts) is a
 * deliberate fork of this — it has to outlive the component — so a fix here
 * probably belongs there too.
 */
export function useRowAccordion<T extends WithRowId>(
  createRow: () => T,
  initialRows?: T[],
) {
  // Each fresh row's values as created, to tell a row the user has started
  // from one they have not touched. Rows handed to `initRows` get none: they
  // already hold the user's work.
  const [pristine] = useState(() => new WeakMap<T, string>());
  const remember = (row: T) => {
    pristine.set(row, snapshot(row));
    return row;
  };

  const [state, setState] = useState<{ rows: T[]; expandedRowId: string }>(
    () => {
      const rows = (initialRows ?? []).map(remember);
      return { rows, expandedRowId: rows[0]?.__rowId ?? "" };
    },
  );

  function initRows(rows: T[]) {
    setState({ rows, expandedRowId: rows[0]?.__rowId ?? "" });
  }

  function addRow() {
    const newRow = remember(createRow());
    setState((prev) => ({
      rows: [...prev.rows, newRow],
      expandedRowId: newRow.__rowId,
    }));
  }

  function removeRow(rowId: string) {
    setState((prev) => {
      const index = prev.rows.findIndex((r) => r.__rowId === rowId);
      if (index === -1) return prev;
      const rows = prev.rows.filter((r) => r.__rowId !== rowId);
      const expandedRowId =
        prev.expandedRowId === rowId
          ? (rows[index]?.__rowId ?? rows[index - 1]?.__rowId ?? "")
          : prev.expandedRowId;
      return { rows, expandedRowId };
    });
  }

  function toggleRow(rowId: string) {
    setState((prev) => ({
      ...prev,
      expandedRowId: prev.expandedRowId === rowId ? "" : rowId,
    }));
  }

  return {
    rows: state.rows,
    expandedRowId: state.expandedRowId,
    initRows,
    addRow,
    removeRow,
    toggleRow,
    startedRowCount: () =>
      state.rows.filter((r) => pristine.get(r) !== snapshot(r)).length,
  };
}

export function RowAccordion<T extends WithRowId>({
  rows,
  expandedRowId,
  toggleRow,
  removeRow,
  addRow,
  summaryLabel,
  renderRow,
  isRowComplete,
  keepHeight,
}: {
  rows: T[];
  expandedRowId: string;
  toggleRow: (rowId: string) => void;
  removeRow: (rowId: string) => void;
  addRow: () => void;
  summaryLabel: (row: T, index: number) => React.ReactNode;
  renderRow: (row: T, index: number) => React.ReactNode;
  /** Marks a folded row the submit button is waiting on. */
  isRowComplete?: (row: T) => boolean;
  /**
   * Grow with the rows but never shrink while open, so folding or removing a
   * row leaves the dialog, and the buttons under the list, where they were.
   */
  keepHeight?: boolean;
}) {
  const { t } = useLingui();
  const idPrefix = useId();
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    const scroller = list?.parentElement;
    if (!keepHeight || !list || !scroller) return;
    const hold = () => {
      const shown =
        scroller.clientHeight - (scroller.scrollHeight - list.offsetHeight);
      list.style.minHeight = `${Math.min(list.offsetHeight, shown)}px`;
    };
    // Measured now too: the observer's first report waits for a rendering
    // frame, and a fold before it would be taken as the height to hold.
    hold();
    const observer = new ResizeObserver(hold);
    observer.observe(list);
    return () => observer.disconnect();
  }, [keepHeight]);

  return (
    <div ref={listRef} className="flex flex-col gap-2">
      {rows.map((row, index) => {
        const isExpanded = expandedRowId === row.__rowId;
        const summaryId = `${idPrefix}-${row.__rowId}-summary`;
        const removeId = `${idPrefix}-${row.__rowId}-remove`;
        const panelId = `${idPrefix}-${row.__rowId}-panel`;
        const incomplete = !isExpanded && isRowComplete?.(row) === false;
        return (
          <div key={row.__rowId} className="rounded-md border border-border">
            <div
              className={cn(
                "flex w-full items-center justify-between px-3 py-2 hover:bg-muted",
                isExpanded ? "rounded-t-md" : "rounded-md",
              )}
            >
              <button
                id={summaryId}
                type="button"
                aria-expanded={isExpanded}
                aria-controls={isExpanded ? panelId : undefined}
                className="flex flex-1 items-center gap-2 bg-transparent p-0 text-left text-sm font-medium"
                onClick={() => toggleRow(row.__rowId)}
              >
                {isExpanded ? (
                  <ChevronDown
                    size={14}
                    className="shrink-0 text-muted-foreground"
                  />
                ) : (
                  <ChevronRight
                    size={14}
                    className="shrink-0 text-muted-foreground"
                  />
                )}
                {/* The callbacks read the caller's observable rows, so they have
                    to run in a tracked render — this component isn't one. */}
                <Observer>{() => <>{summaryLabel(row, index)}</>}</Observer>
                {incomplete && (
                  <Tooltip content={t`Required fields are missing`}>
                    <span className="text-warning-foreground ml-auto inline-flex shrink-0">
                      <CircleAlert size={14} aria-hidden />
                      <span className="sr-only">
                        <Trans context="row">Incomplete</Trans>
                      </span>
                    </span>
                  </Tooltip>
                )}
              </button>
              {rows.length > 1 && (
                // Named after its row as well — every row has one, and "Remove"
                // alone doesn't say which it removes. The self-reference picks
                // up the button's own `aria-label`.
                <Button
                  variant="tertiary"
                  size="sm"
                  destructive
                  icon={Trash2}
                  label={t`Remove`}
                  id={removeId}
                  aria-labelledby={`${removeId} ${summaryId}`}
                  className="ml-2"
                  onClick={() => removeRow(row.__rowId)}
                />
              )}
            </div>
            {isExpanded && (
              <div
                id={panelId}
                className="border-t border-border px-4 py-3 overflow-hidden"
              >
                <Observer>{() => <>{renderRow(row, index)}</>}</Observer>
              </div>
            )}
          </div>
        );
      })}
      <Button type="button" size="sm" icon={Plus} onClick={addRow}>
        <Trans>Add row</Trans>
      </Button>
    </div>
  );
}
